import { formatDate, moduleTypeToLabel } from '@/lib/utils';
import {
  REVIEW_CHANGE_BADGE_CONFLICT,
  REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT,
  REVIEW_CHANGE_BADGE_CONFIG,
} from '@/lib/constants/manuscripts/store';
import {
  proseMirrorToHtml,
  hasProseMirrorText,
  flattenParagraphRuns,
  renderMarkedText,
  renderNebulaIcon,
} from './proseMirrorHtml';

const CONTENT_NOT_PREVIEWABLE = 'This content type isn\'t previewable yet.';

// A badge the backend sends that isn't in this config is dropped rather than guessed at.
const KNOWN_BADGES = new Set(Object.keys(REVIEW_CHANGE_BADGE_CONFIG));

function sideTime(side) {
  return side?.updated_on ? formatDate(side.updated_on, true) : null;
}

// manuscript's own Page.number vs. Studio's 1-indexed sort_index rank -- can differ after a reorder.
function slideLabel(side) {
  return Number.isFinite(side?.order) ? `Slide ${side.order}` : null;
}

function hasText(paragraphs) {
  return paragraphs.some((paragraph) => paragraph.some((segment) => segment.text));
}

// Studio has no explicit "teacher note" field on a module -- newModuleTemplate() in Studio's
// own store.js hardcodes layout.width = 1 only for notes-canvas modules, so a synced note is
// the only text module that ever reports a width of exactly 1.
function isTeacherNote(item) {
  return item.studio?.content?.layout?.width === 1;
}

// A whole paragraph added/removed outright has no segments -- synthesize one from old/new.
function paragraphSegments(paragraph) {
  if (paragraph.segments) return paragraph.segments;
  if (paragraph.op === 'insert') return paragraph.new ? [{ op: 'insert', text: paragraph.new }] : [];
  if (paragraph.op === 'delete') return paragraph.old ? [{ op: 'delete', text: paragraph.old }] : [];
  return [
    ...(paragraph.old ? [{ op: 'delete', text: paragraph.old }] : []),
    ...(paragraph.new ? [{ op: 'insert', text: paragraph.new }] : []),
  ];
}

// An 'equal' paragraph has identical old/new text -- either works as the plain text.
function paragraphText(paragraph) {
  return paragraph.new ?? paragraph.old ?? '';
}

// Splits `runs` (from flattenParagraphRuns) at the char offsets `segments` implies.
// Only `consumingOp` (+'equal') consumes runs -- the other op never existed in this
// doc, so it's emitted as plain highlighted text. Zero-width icon/break runs flush the
// moment the cursor reaches them, even mid-segment, instead of being skipped.
function alignRunsWithSegments(runs, segments, highlightByOp, consumingOp) {
  let runIdx = 0;
  let offsetInRun = 0;
  const consume = (len) => {
    const pieces = [];
    let remaining = len;
    while (runIdx < runs.length) {
      const run = runs[runIdx];
      if (run.icon || run.break) {
        pieces.push(run.icon ? { icon: run.icon } : { break: true });
        runIdx += 1;
      } else if (remaining <= 0) {
        break;
      } else {
        const take = Math.min(run.text.length - offsetInRun, remaining);
        if (take > 0) pieces.push({ text: run.text.substr(offsetInRun, take), marks: run.marks });
        offsetInRun += take;
        remaining -= take;
        if (offsetInRun >= run.text.length) {
          runIdx += 1;
          offsetInRun = 0;
        }
      }
    }
    return pieces;
  };

  return segments.flatMap((segment) => {
    const highlight = highlightByOp[segment.op] ?? null;
    if (segment.op !== 'equal' && segment.op !== consumingOp) {
      return segment.text ? [{ text: segment.text, marks: [], highlight }] : [];
    }
    return consume(segment.text.length).map((piece) => ({ ...piece, highlight }));
  });
}

function highlightWrap(html, highlight) {
  if (!highlight) return html;
  return `<span class="review-change-rich-text__highlight review-change-rich-text__highlight--${highlight}">${html}</span>`;
}

function renderAlignedRun(run) {
  if (run.icon) return renderNebulaIcon(run.icon);
  if (run.break) return '<br>';
  return renderMarkedText(run.text, run.marks);
}

// text_diff's insert/delete are always manuscript -> studio, so a 'delete' entry has no
// counterpart in studio's real doc, and an 'insert' entry has no counterpart in manuscript's.
function existsOnSide(paragraph, side) {
  return side === 'studio' ? paragraph.op !== 'delete' : paragraph.op !== 'insert';
}

// proseMirrorToHtml `renderParagraph` override: splices text_diff highlighting onto
// paragraph[index]'s real runs (index = doc order, filtered to entries that exist on
// editedSide -- a raw index would desync past any one-sided paragraph); 'equal' renders as-is.
function buildRenderParagraph(paragraphs, editedSide, highlightByOp) {
  const consumingOp = editedSide === 'studio' ? 'insert' : 'delete';
  const sideParagraphs = paragraphs.filter((paragraph) => existsOnSide(paragraph, editedSide));
  return (node, index) => {
    const diffEntry = sideParagraphs[index];
    if (!diffEntry || diffEntry.op === 'equal') return null;
    const runs = flattenParagraphRuns(node);
    const segments = paragraphSegments(diffEntry);
    const aligned = alignRunsWithSegments(runs, segments, highlightByOp, consumingOp);
    return aligned
      .map((run) => highlightWrap(renderAlignedRun(run), run.highlight))
      .join('');
  };
}

// A side's raw ProseMirror JSON (studio: options.content; manuscript: text when
// text_type is 'prosemirror'); null otherwise -- callers fall back to flat rendering.
function rawProseMirrorDoc(item, sideKey) {
  const side = item[sideKey];
  if (sideKey === 'studio') return side?.content?.options?.content ?? null;
  return side?.content?.text_type === 'prosemirror' ? (side?.content?.text ?? null) : null;
}

function parsedProseMirrorDoc(raw) {
  if (!raw) return null;
  try {
    const doc = JSON.parse(raw);
    return hasProseMirrorText(doc) ? doc : null;
  } catch {
    return null;
  }
}

// Real doc + text_diff highlighting spliced in; null (no doc/bad JSON/no text) falls back to flat paragraphs.
function richEditedHtml(item, editedSide, paragraphs, highlightByOp) {
  const doc = parsedProseMirrorDoc(rawProseMirrorDoc(item, editedSide));
  if (!doc) return null;
  return proseMirrorToHtml(doc, { renderParagraph: buildRenderParagraph(paragraphs, editedSide, highlightByOp) });
}

// The untouched side has nothing to diff -- render its own real doc as-is, no highlighting.
function richPlainHtml(item, sideKey) {
  const doc = parsedProseMirrorDoc(rawProseMirrorDoc(item, sideKey));
  return doc ? proseMirrorToHtml(doc) : null;
}

// Prefer real-doc HTML, then flat paragraphs, then the placeholder.
function contentSlotFields(richHtml, flatParagraphs) {
  if (richHtml) return { html: richHtml };
  if (hasText(flatParagraphs)) return { paragraphs: flatParagraphs };
  return { text: CONTENT_NOT_PREVIEWABLE };
}

function buildSlot(side, richHtml, flatParagraphs) {
  return {
    slideLabel: slideLabel(side),
    time: sideTime(side),
    ...contentSlotFields(richHtml, flatParagraphs),
  };
}

// text_diff's delete/insert are always manuscript -> studio; editedSide only flips which reads as added vs removed.
function buildEditedSides(item, editedSide) {
  const paragraphs = item.text_diff?.paragraphs ?? [];
  const highlightByOp = editedSide === 'manuscript'
    ? { delete: 'added', insert: 'removed' }
    : { delete: 'removed', insert: 'added' };
  const editedParagraphs = paragraphs.map((paragraph) => (paragraph.op === 'equal'
    ? [{ text: paragraphText(paragraph), highlight: null }]
    : paragraphSegments(paragraph).map((segment) => ({
      text: segment.text,
      highlight: highlightByOp[segment.op] ?? null,
    }))));
  const plainText = editedSide === 'manuscript' ? 'new' : 'old';
  // Flat fallback only: a paragraph new-only on the edited side renders blank here, but still positioned.
  const untouchedParagraphs = paragraphs.map((paragraph) => [{
    text: paragraph.op === 'equal' ? paragraphText(paragraph) : (paragraph[plainText] ?? ''),
    highlight: null,
  }]);

  const untouchedSide = editedSide === 'manuscript' ? 'studio' : 'manuscript';
  return {
    [editedSide]: buildSlot(item[editedSide], richEditedHtml(item, editedSide, paragraphs, highlightByOp), editedParagraphs),
    [untouchedSide]: buildSlot(item[untouchedSide], richPlainHtml(item, untouchedSide), untouchedParagraphs),
  };
}

// Conflict: both sides diverged independently, so each shows its own real text (equal plain, its own segments highlighted).
function buildConflictSides(item) {
  const paragraphs = item.text_diff?.paragraphs ?? [];
  const manuscriptParagraphs = paragraphs.map((paragraph) => (paragraph.op === 'equal'
    ? [{ text: paragraphText(paragraph), highlight: null }]
    : paragraphSegments(paragraph)
      .filter((segment) => segment.op === 'equal' || segment.op === 'delete')
      .map((segment) => ({ text: segment.text, highlight: segment.op === 'delete' ? 'removed' : null }))));
  const studioParagraphs = paragraphs.map((paragraph) => (paragraph.op === 'equal'
    ? [{ text: paragraphText(paragraph), highlight: null }]
    : paragraphSegments(paragraph)
      .filter((segment) => segment.op === 'equal' || segment.op === 'insert')
      .map((segment) => ({ text: segment.text, highlight: segment.op === 'insert' ? 'added' : null }))));

  return {
    manuscript: buildSlot(item.manuscript, null, manuscriptParagraphs),
    studio: buildSlot(item.studio, null, studioParagraphs),
  };
}

function buildDiffedSides(item) {
  if (item.badge === REVIEW_CHANGE_BADGE_CONFLICT) return buildConflictSides(item);
  return buildEditedSides(item, item.badge === REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT ? 'manuscript' : 'studio');
}

// manuscript side is a curated { target_url, external_url }; Studio side is
// the module's raw options doc, where a URL only exists for options.action === 'link'.
function linkUrl(side, isStudio) {
  if (isStudio) {
    const options = side.content?.options ?? {};
    return options.action === 'link' ? (options.target ?? null) : null;
  }
  return side.content?.target_url || side.content?.external_url || null;
}

const CONTENT_TYPE_LABELS = {
  internal_asset: 'Asset',
  link: 'Link',
};

function humanize(value) {
  if (!value) return '';
  return value.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function contentTypeLabel(item) {
  if (isTeacherNote(item)) return 'Teacher Note';
  if (!item.content_type) return 'Text';
  return CONTENT_TYPE_LABELS[item.content_type] || humanize(item.content_type);
}

function notPreviewableText(contentType, side) {
  const moduleType = moduleTypeToLabel(side?.content?.type);
  const detail = moduleType && moduleType !== contentType ? ` (${moduleType})` : '';
  return `${contentType} content${detail} isn't previewable here yet.`;
}

// manuscript is a curated { text, text_type }; studio's text (if any) is ProseMirror
// JSON at options.content. Returns { text } for a plain string, { html } for ProseMirror.
function richTextFromContent(side, isStudio) {
  const raw = isStudio ? side.content?.options?.content : side.content?.text;
  if (!raw) return null;
  if (!isStudio && side.content?.text_type !== 'prosemirror') return { text: raw };
  try {
    const doc = JSON.parse(raw);
    return hasProseMirrorText(doc) ? { html: proseMirrorToHtml(doc) } : null;
  } catch {
    return null;
  }
}

// assetLinkUrl mirrors the "Open Asset" link Studio's own MiniMenu builds.
// manuscript side is a curated { asset_id }; Studio side nests it under options,
// same as an asset module's own moduleDefaults shape (options.asset_id).
function buildNoDiffSide(side, isStudio, appDomain, contentType) {
  const assetId = isStudio ? side.content?.options?.asset_id : side.content?.asset_id;
  const url = linkUrl(side, isStudio);
  const richText = !assetId && !url ? richTextFromContent(side, isStudio) : null;
  return {
    slideLabel: slideLabel(side),
    time: sideTime(side),
    ...(assetId && { assetId, ...(appDomain && { assetLinkUrl: `${appDomain}/player/${assetId}` }) }),
    ...(url && { linkUrl: url }),
    ...richText,
    ...(!assetId && !url && !richText && { text: notPreviewableText(contentType, side) }),
  };
}

function buildAddedSides(item, appDomain) {
  const contentType = contentTypeLabel(item);
  return {
    manuscript: item.manuscript ? buildNoDiffSide(item.manuscript, false, appDomain, contentType) : null,
    studio: item.studio ? buildNoDiffSide(item.studio, true, appDomain, contentType) : null,
  };
}

// STUDSYNC-472's orthogonal contract: `change` is one of modify/add/remove/reorder
// (collapsed from the old modified/add/unmatched/missing/removed/reorder six).
const CHANGE_LABELS = {
  modify: 'Modified',
  add: 'Added',
  remove: 'Removed',
};

function changeLabel(item) {
  return CHANGE_LABELS[item.change] || humanize(item.change);
}

function changeTypeLabel(item) {
  return [contentTypeLabel(item), changeLabel(item)].filter(Boolean).join(' ');
}

function cardTitle(item) {
  const primary = item.section?.title || item.label || 'Untitled';
  const changeType = changeTypeLabel(item);
  const showLabel = !!(item.section?.title && item.label);
  const parts = [primary];
  if (changeType) parts.push(showLabel ? `${changeType}: ${item.label}` : changeType);
  return parts.join(' | ');
}

function navLabel(item) {
  const primary = item.section?.title || item.label || 'Untitled';
  const changeType = changeTypeLabel(item);
  const parts = [primary];
  if (changeType) parts.push(changeType);
  if (item.page?.order != null) parts.push(`Page: ${item.page.order}`);
  return parts.join(' | ');
}

/**
 * Maps the live GET /manuscripts/<id>/studio-diff response into what
 * ReviewChangeCard expects. Only items with `can_sync: true` and a badge the
 * panel recognizes are kept -- everything else (structural section/page
 * entries, content types Studio can't sync at all) is dropped.
 *
 * @param {object} diff - the studio-diff response body.
 * @param {string} [appDomain] - `domains.appDomain` from the store, used to
 *   build an internal_asset's "Open Asset" link. Omit to skip that link.
 */
export function mapStudioDiffToReviewChanges(diff, appDomain) {
  const changes = diff?.changes ?? [];
  return changes
    .filter((item) => item.can_sync && KNOWN_BADGES.has(item.badge))
    .map((item) => ({
      id: item.id,
      title: cardTitle(item),
      navLabel: navLabel(item),
      badge: item.badge,
      isTeacherNote: isTeacherNote(item),
      ...(item.text_diff ? buildDiffedSides(item) : buildAddedSides(item, appDomain)),
    }));
}

export default mapStudioDiffToReviewChanges;
